import * as React from 'react';

import { ErrorTracker } from '@futrli/shared-services';
import { toastError } from '@futrli/ui-components';
import { FormattedMessage } from '@futrli/localisation';

import { ERROR_STATUS } from '../core/constants';
import { AnyError } from '../core/types';
import { getErrorStatus } from '../utils/utils';
import { shouldRaiseErrorToErrorBoundary } from '../utils/with-error-boundary-utils';

/**
 * useErrorHandler is an alternative to `withErrorBoundary`. Preference using this
 * module when you are wanting to use error handling within the context of functional
 * components with Hooks. It is particularly useful for handling asynchronous issues,
 * as the error can be passed directly to the error handler within a callback.
 *
 * @returns {function} The error handler to use within your component.
 */
export const useErrorHandler = () => {
  /**
   * Manage the error passed as a parameter. If the error is an specified error to be rendered as an error page
   * component, then raise it to ErrorBoundary component, if not, log the error in Sentry.
   * In case we want to avoid some error codes, they can be passed as an array.
   *
   * @param {AnyError | null} error
   * @param {ERROR_STATUS[] | []} ignoreErrors
   */
  return (error: AnyError | null, ignoreErrors: ERROR_STATUS[] = []) => {
    const errorStatus = getErrorStatus(error);

    if (errorStatus && ignoreErrors.includes(errorStatus)) {
      return;
    }

    if (shouldRaiseErrorToErrorBoundary(error)) {
      let errorMessage;
      if (errorStatus === ERROR_STATUS._403) {
        errorMessage = (
          <FormattedMessage
            id="errors.not_have_permission"
            defaultMessage="You do not have permission to perform this action."
          />
        );
      }
      toastError(errorMessage);
    } else {
      ErrorTracker.captureException(error);
    }
  };
};
